import type { StoreSubscriptionHistoryEntry } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import { formatDate, formatDateTime } from "@/lib/formatting/date-time";
import { EmptyView, Timeline, type TimelineItem, type VizTone } from "@/shared/rd";

const TONE: Record<string, VizTone> = {
  ACTIVATED: "blue",
  RENEWED: "good",
  CANCELLED: "bad",
  CANCELLATION_SCHEDULED: "warn",
  RENEWAL_WARNING: "warn",
  INSUFFICIENT_FUNDS: "bad",
  RENEWAL_MISSED: "bad",
  COMPLIMENTARY_GRANTED: "purple",
  COMPLIMENTARY_REVOKED: "ink",
};

const ACTION_LABEL: Record<string, string> = {
  ACTIVATED: keys.admin.tierEventActivated,
  CANCELLED: keys.admin.tierEventCancelled,
  CANCELLATION_SCHEDULED: keys.admin.tierEventCancellationScheduled,
  RENEWED: keys.admin.tierEventRenewed,
  RENEWAL_WARNING: keys.admin.tierEventRenewalWarning,
  INSUFFICIENT_FUNDS: keys.admin.tierEventInsufficientFunds,
  RENEWAL_MISSED: keys.admin.tierEventRenewalMissed,
  COMPLIMENTARY_GRANTED: keys.admin.tierEventComplimentaryGranted,
  COMPLIMENTARY_REVOKED: keys.admin.tierEventComplimentaryRevoked,
};

/**
 * A DEFAULT store's subscription history: every lifecycle decision (activate,
 * cancel now / at period end, renewal, insufficient funds, complimentary
 * granted / revoked), newest first, with who made it and when. Assignments
 * alone could not tell these apart.
 */
export function StoreLifecycleHistory({
  entries,
  locale,
}: {
  entries: StoreSubscriptionHistoryEntry[];
  locale: Locale;
}) {
  const t = useTranslations();
  if (entries.length === 0) {
    return <EmptyView illustration="admin-billing" tone="blue" title={t(keys.admin.tierHistoryEmpty)} sub={t("admin.subscriptionHistoryEmptySub")} />;
  }
  const money = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  };
  const items: TimelineItem[] = entries.map((entry) => ({
    id: String(entry.revision),
    time: (
      <time dateTime={entry.occurredAt} className="rd-num">
        {formatDate(entry.occurredAt, locale)}
      </time>
    ),
    tone: TONE[entry.action] ?? "ink",
    filled: entry.action !== "RENEWAL_WARNING",
    label: t(ACTION_LABEL[entry.action] ?? keys.admin.tierEventOther),
    note: [
      formatDateTime(entry.occurredAt, locale),
      entry.admin
        ? t(keys.admin.tierHistoryBy, { name: entry.admin.name || entry.admin.id.slice(0, 8) })
        : t(keys.admin.tierHistorySystem),
    ].join(" · "),
    amount:
      entry.plan && (entry.action === "ACTIVATED" || entry.action === "RENEWED") ? (
        <span className="rd-num">
          {pickLocalized(locale, entry.plan.nameAr, entry.plan.nameEn)} {money(entry.plan.amount, entry.plan.currency)}
        </span>
      ) : undefined,
  }));
  return <Timeline items={items} />;
}
